import type {
  GridProps
} from '@chakra-ui/react'
import {
  Flex,
  forwardRef,
  SkeletonCircle,
  SkeletonText,
  Text,
} from '@chakra-ui/react'
import type { AppInfoMessage } from 'BrokerTsLib/src/dtos'
import {
  useGetAllAppInfoMessages,
  useSetReadAppInfoMessage,
} from 'brokertslib/src/hooks/useAppInfoMessage'
import { useEffect } from 'react'
import { useNavigate } from 'react-router'

import { useApp } from '@/hooks/useApp'

import { useTranslationsContext } from 'BrokerTsLib/src/context/TranslationsContext'
import { News } from '../../components/News'
import { DashboardCard } from './DashboardCard'

export type NewsCardProps = GridProps

export const NewsCard = forwardRef(
  ({ children, ...rest }: NewsCardProps, ref) => {
    const { product } = useApp()
    const navigate = useNavigate()
    const { translate } = useTranslationsContext()

    const {
      mutateAsync: getMessages,
      data: dataMessages,
      isPending,
    } = useGetAllAppInfoMessages()
    const filter = {
      product: product!,
      pageIndex: 1,
      pageSize: 5,
      shown: true,
    }
    const allNews = (dataMessages?.data?.items ?? []) as AppInfoMessage[]

    useEffect(() => {
      getMessages(filter)
    }, [filter.product])

    const msgs = allNews.sort(
      (a, b) =>
        new Date(b.messageDate ?? b.dateCreatedMember).getTime() -
        new Date(a.messageDate ?? a.dateCreatedMember).getTime(),
    )

    const showMessageLimit = 3
    const limitedNews = msgs.slice(0, showMessageLimit)

    const { mutateAsync: setReadAsync } = useSetReadAppInfoMessage()

    const setRead = (id: number) => {
      setReadAsync({ read: true, id }).then(() => {
        getMessages(filter)
      })
    }

    return (
      <DashboardCard ref={ref} title={translate('ui.Dashboard.News.Title', 'Aktuality a informace')} {...rest}>
        {isPending ? (
          <Flex gap={6} pl={8} pr={8} pt={4} pb={8}>
            <SkeletonCircle size="10" />
            <SkeletonText flexGrow={1} />
          </Flex>
        ) : (
          <>
            {limitedNews.length === 0 && (
              <Text
                sx={{
                  pl: {
                    base: 4,
                    md: 6
                  },
                  pb: {
                    md: 2
                  }
                }}
              >
                {translate('ui.Dashboard.News.Empty', 'Aktuálně nejsou vypsány žádné novinky')}
              </Text>
            )}
            <News
              news={limitedNews}
              onSetRead={setRead}
              sx={{
                px: {
                  base: 4,
                  md: 8,
                },
              }}
              onMoreClick={
                limitedNews.length < msgs.length
                  ? () => navigate('news')
                  : undefined
              }
            />
          </>
        )}
      </DashboardCard>
    )
  },
)
